/**
 * The picture at the top of a programme.
 *
 * A real poster when the committee has uploaded one. When they have not —
 * which is most of the time, and always for a brand-new programme — the
 * category gets its own colour field and a kolam motif rather than a grey box
 * or, worse, a stock photograph of the wrong art form. A diya picture
 * standing in for a drama evening is a lie; a drawn pattern is honestly
 * decorative.
 *
 * The motif is a pulli kolam: a grid of dots with a loop drawn around each,
 * which is what gets drawn in rice flour at a Tamil threshold. It is woven
 * into the colour rather than stamped on top, so it reads as texture at a
 * glance and as something particular when looked at.
 */
import { useTranslation } from "react-i18next";

export function ProgramArt({
  poster,
  category,
  labelKey,
  tall = false,
}: {
  poster?: string | null;
  category?: string | null;
  labelKey?: string | null;
  /** Detail screens give it more height than a list card does. */
  tall?: boolean;
}) {
  const { t } = useTranslation();
  const className = `art${tall ? " art--tall" : ""}`;

  if (poster) {
    return (
      <div className={className}>
        <img className="art__photo" src={poster} alt="" loading="lazy" />
      </div>
    );
  }

  return (
    <div className={className} data-category={category ?? "none"}>
      <svg className="art__kolam" aria-hidden="true" focusable="false">
        <defs>
          <pattern id="kolam" width="44" height="44" patternUnits="userSpaceOnUse">
            {/* The dot, and the loop drawn around it. */}
            <circle cx="22" cy="22" r="1.7" fill="#fff" fillOpacity="0.55" />
            <path
              d="M22 6 Q38 22 22 38 Q6 22 22 6 Z"
              fill="none"
              stroke="#fff"
              strokeOpacity="0.16"
              strokeWidth="1.1"
            />
            <circle cx="0" cy="0" r="1.7" fill="#fff" fillOpacity="0.28" />
            <circle cx="44" cy="44" r="1.7" fill="#fff" fillOpacity="0.28" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#kolam)" />
      </svg>
      {labelKey && <span className="art__label">{t(labelKey)}</span>}
    </div>
  );
}
